import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import moment from 'moment'

import {
  checkoutCookbook,
  cookbookError,
  getCookbook,
  getCookbookQuote,
  requestCookbookOrder,
  US_STATES,
} from '../../../../api/cookbook'
import { openCheckout } from '../../../../utils/chargebee'
import { getVouchers } from '../../../../api/gifts'
import routes from '../../../../routers'
import { ArrowBack } from '../../../../components/SvgIcon'
import CustomTitle from '../CustomTitle'

import styles from './CheckoutStep.module.css'

const dollars = (n) => `$${Number(n).toFixed(2)}`
const arrives = (o) =>
  o?.delivery_from && o?.delivery_to
    ? `Arrives ${moment(o.delivery_from).format('MMM D')} to ${moment(o.delivery_to).format(
        'MMM D'
      )}`
    : ''

const FIELDS = [
  { key: 'name', label: 'Full name', auto: 'name' },
  { key: 'street1', label: 'Street address', auto: 'address-line1' },
  { key: 'street2', label: 'Apartment, suite (optional)', auto: 'address-line2', optional: true },
  { key: 'city', label: 'City', auto: 'address-level2' },
]

// What the Morsel team does next, once a book is requested
const RequestedNote = ({ request, fresh, email }) => (
  <div className={styles.requested} role="status">
    <p className={styles.requestedTitle}>
      <span className={styles.requestedBadge}>Requested</span>
      Your book is saved
    </p>
    <p>
      We’ll email you{email ? ` at ${email}` : ''} within 1 business day to confirm the details and
      take payment. Then it goes to the printer.
    </p>
    {fresh ? (
      <p className="cbMuted">We sent a copy to your email too.</p>
    ) : request?.updated_at ? (
      <p className="cbMuted">
        Requested {moment(request.updated_at).format('MMM D [at] h:mm a')}. Changed your mind on
        something? Update the details below and send it again.
      </p>
    ) : null}
  </div>
)

// Step 5: where to ship, how many, how fast. With online checkout (user.cookbook_self_serve),
// Chargebee's secure payment screen, shown over the Morsel page. Without it, "Request my
// book": the details are saved and the Morsel team emails them to finish the order.
const CheckoutStep = ({ orderUrlData }) => {
  const user = useSelector((state) => state.user?.data)
  const selfServe = !!user?.cookbook_self_serve
  const navigate = useNavigate()
  const { orderId } = orderUrlData
  const [form, setForm] = useState({
    name: user?.fullname || '',
    street1: '',
    street2: '',
    city: '',
    state_code: '',
    postcode: '',
    phone_number: '',
    quantity: 1,
  })
  const [quote, setQuote] = useState(null)
  const [level, setLevel] = useState('')
  const [quoteError, setQuoteError] = useState('')
  const [fields, setFields] = useState({})
  const [error, setError] = useState('')
  const [paying, setPaying] = useState(false)
  // A gift: shipped to someone else (the card's billing address is asked separately)
  const [isGift, setIsGift] = useState(false)
  // Cookbook vouchers on their account (from a gift)
  const [vouchers, setVouchers] = useState([])
  const [voucherId, setVoucherId] = useState('')
  // An earlier "Request my book" for this book that the team hasn't finished yet
  const [request, setRequest] = useState(null)
  const [justRequested, setJustRequested] = useState(false)

  // Requested before: show it, with the details they sent
  useEffect(() => {
    if (!orderId) return
    getCookbook(orderId)
      .then(({ data }) => {
        const saved = data?.order_request
        if (!saved) return
        setRequest(saved)
        const a = saved.shipping_address || {}
        setForm((old) => ({
          ...old,
          name: a.name || old.name,
          street1: a.street1 || '',
          street2: a.street2 || '',
          city: a.city || '',
          state_code: a.state_code || '',
          postcode: a.postcode || '',
          phone_number: a.phone_number || '',
          quantity: saved.quantity || 1,
        }))
        setLevel(saved.shipping_level || '')
        setIsGift(!!saved.is_gift)
      })
      .catch(() => null)
  }, [orderId])

  useEffect(() => {
    getVouchers()
      .then(({ data }) => {
        setVouchers(data || [])
        if (data?.length) setVoucherId(String(data[0].id))
      })
      .catch(() => setVouchers([]))
  }, [])

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })
  const option = useMemo(() => quote?.options?.find((o) => o.level === level), [quote, level])
  const ready = form.state_code && /^\d{5}$/.test(form.postcode)
  const voucher = vouchers.find((v) => String(v.id) === voucherId)
  const voucherOff = option && voucher ? Math.min(voucher.value, option.total) : 0
  const due = option ? Math.max(0, option.total - voucherOff) : 0

  // Shipping speeds and prices follow the state, ZIP code and number of copies
  useEffect(() => {
    if (!ready) return setQuote(null)
    setQuoteError('')
    getCookbookQuote(orderId, {
      country_code: 'US',
      state_code: form.state_code,
      postcode: form.postcode,
      city: form.city || null,
      quantity: Number(form.quantity),
    })
      .then(({ data }) => {
        setQuote(data)
        if (!data.options.some((o) => o.level === level)) setLevel(data.options[0]?.level || '')
      })
      .catch((e) => {
        setQuote(null)
        setQuoteError(cookbookError(e, 'We could not get shipping prices for that ZIP code.'))
      })
  }, [form.state_code, form.postcode, form.quantity])

  const details = () => ({
    ...form,
    quantity: Number(form.quantity),
    shipping_level: level,
    is_gift: isGift,
    voucher_id: voucher ? voucher.id : null,
  })

  const ask = () => {
    setPaying(true)
    requestCookbookOrder(orderId, details())
      .then(({ data }) => {
        setPaying(false)
        setRequest(data)
        setJustRequested(true)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
      .catch((err) => {
        setPaying(false)
        setFields(err?.response?.data?.fields || {})
        setError(cookbookError(err, 'Something went wrong. Please try again.'))
      })
  }

  const pay = (e) => {
    e.preventDefault()
    setError('')
    setFields({})
    if (!option) return setError('Choose a shipping speed')
    if (!selfServe) return ask()
    setPaying(true)
    checkoutCookbook(orderId, {
      ...details(),
    })
      .then(async ({ data }) => {
        // Paid in full by a voucher: no card needed
        if (data.paid) return navigate(`${routes.cookbookOrders}/${data.order_id}?paid=1`)
        // If the pop-up can't load, fall back to Chargebee's full payment page
        const result = await openCheckout(data.chargebee_site, data.hosted_page).catch(() => null)
        if (!result) {
          window.location.href = data.url
          return
        }
        if (result.paid) return navigate(`${routes.cookbookOrders}/${data.order_id}?paid=1`)
        setPaying(false)
      })
      .catch((err) => {
        setPaying(false)
        setFields(err?.response?.data?.fields || {})
        setError(cookbookError(err, 'Something went wrong. Please try again.'))
      })
  }

  return (
    <div className="cbStep">
      {!selfServe && request ? (
        <RequestedNote request={request} fresh={justRequested} email={user?.email} />
      ) : null}
      {!selfServe && !request ? (
        <div className={styles.soon}>
          <p className={styles.soonTitle}>Online checkout is coming soon</p>
          <p>
            Tell us where to send your book and press <b>Request my book</b>. Your book is saved,
            and the Morsel team will email you within 1 business day to finish your order and take
            payment. Ordering for the holidays? Request early so it arrives in time.
          </p>
        </div>
      ) : null}
      <form className={styles.layout} onSubmit={pay}>
        <section className="cbSection">
          <CustomTitle title="Ship to" />
          <label className={`cbChoice ${isGift ? 'cbChoiceOn' : ''}`}>
            <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)} />
            <span className="cbChoiceDot" />
            <span className="cbChoiceBody">
              <b>This is a gift</b>
              {selfServe
                ? 'Ship it to someone else. Your card’s billing address is asked for separately.'
                : 'Ship it to someone else.'}
            </span>
          </label>
          {isGift ? (
            <p className="cbMuted">
              Your gift note is the first page of the book, the dedication. To change it, go back to
              the cover step and preview the book again.
            </p>
          ) : null}
          {FIELDS.map((f) => (
            <label key={f.key} className={styles.field}>
              {isGift && f.key === 'name' ? 'Their full name' : f.label}
              <input
                value={form[f.key]}
                onChange={set(f.key)}
                autoComplete={f.auto}
                required={!f.optional}
                maxLength={100}
                className={`cbInput ${fields[f.key] ? 'cbInputBad' : ''}`}
              />
              {fields[f.key] ? <span className={styles.fieldError}>{fields[f.key]}</span> : null}
            </label>
          ))}
          <div className={styles.row}>
            <label className={styles.field}>
              State
              <select
                value={form.state_code}
                onChange={set('state_code')}
                required
                autoComplete="address-level1"
                className={`cbInput ${fields.state_code ? 'cbInputBad' : ''}`}
              >
                <option value="">Choose…</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              ZIP code
              <input
                value={form.postcode}
                onChange={set('postcode')}
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={10}
                required
                className={`cbInput ${fields.postcode ? 'cbInputBad' : ''}`}
              />
              {fields.postcode ? (
                <span className={styles.fieldError}>{fields.postcode}</span>
              ) : null}
            </label>
          </div>
          <label className={styles.field}>
            {isGift ? 'Their phone (for the delivery driver)' : 'Phone (for the delivery driver)'}
            <input
              value={form.phone_number}
              onChange={set('phone_number')}
              type="tel"
              autoComplete="tel"
              maxLength={20}
              required
              className={`cbInput ${fields.phone_number ? 'cbInputBad' : ''}`}
            />
            {fields.phone_number ? (
              <span className={styles.fieldError}>{fields.phone_number}</span>
            ) : null}
          </label>
          <p className="cbMuted">We ship within the United States.</p>
        </section>

        <section className={`cbSection ${styles.order}`}>
          <CustomTitle title="Your order" />
          <label className={styles.field}>
            Copies
            <select className="cbInput" value={form.quantity} onChange={set('quantity')}>
              {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <p className="cbLabel">Shipping speed</p>
          {!ready ? <p className="cbMuted">Enter the state and ZIP code to see shipping.</p> : null}
          {quoteError ? <p className="cbError">{quoteError}</p> : null}
          {quote?.options?.map((o) => (
            <label key={o.level} className={`cbChoice ${level === o.level ? 'cbChoiceOn' : ''}`}>
              <input
                type="radio"
                name="level"
                checked={level === o.level}
                onChange={() => setLevel(o.level)}
              />
              <span className="cbChoiceDot" />
              <span className="cbChoiceBody">
                <b>{o.name}</b>
                {arrives(o)}
              </span>
              <span className={styles.price}>{dollars(o.shipping)}</span>
            </label>
          ))}

          {option ? (
            <div className={styles.summary}>
              <p>
                <span>
                  {quote.product.name}
                  {quote.product.size_label ? `, ${quote.product.size_label}` : ''},{' '}
                  {quote.page_count} pages
                  {quote.quantity > 1 ? ` x ${quote.quantity}` : ''}
                </span>
                <span>{dollars(option.book_price)}</span>
              </p>
              <p>
                <span>Shipping ({option.name.toLowerCase()})</span>
                <span>{dollars(option.shipping)}</span>
              </p>
              {voucherOff ? (
                <p>
                  <span>Gift voucher</span>
                  <span>−{dollars(voucherOff)}</span>
                </p>
              ) : null}
              <p className={styles.total}>
                <span>{!selfServe ? 'Estimated total' : due ? 'Total before tax' : 'Total'}</span>
                <span>{dollars(due)}</span>
              </p>
              <p className="cbMuted">
                {selfServe
                  ? 'Sales tax, if any, is added on the payment page.'
                  : 'Sales tax, if any, is added when you pay.'}
              </p>
            </div>
          ) : null}

          {vouchers.length ? (
            <label className={styles.field}>
              Pay with a gift voucher
              <select
                className="cbInput"
                value={voucherId}
                onChange={(e) => setVoucherId(e.target.value)}
              >
                <option value="">Don’t use a voucher</option>
                {vouchers.map((v) => (
                  <option key={v.id} value={String(v.id)}>
                    {dollars(v.value)} voucher{v.from ? ` from ${v.from}` : ''}
                  </option>
                ))}
              </select>
              {voucher && option && voucher.value > option.total ? (
                <span className="cbMuted">
                  The voucher is worth more than this order; the{' '}
                  {dollars(voucher.value - option.total)} left over isn’t kept.
                </span>
              ) : null}
            </label>
          ) : null}

          {error ? <p className="cbError">{error}</p> : null}
          <button
            type="submit"
            className={`cbNextButton ${styles.pay}`}
            disabled={!option || paying}
          >
            {paying
              ? 'One moment…'
              : !selfServe
              ? request
                ? 'Update my request'
                : 'Request my book'
              : option && !due
              ? 'Place order (paid by voucher)'
              : 'Continue to secure payment'}
          </button>
          {selfServe ? (
            <p className="cbMuted">
              You pay on Chargebee’s secure page; Morsel never sees your card. You can cancel for a
              full refund within 1 hour of ordering. After that your book goes to the printer.
            </p>
          ) : (
            <p className="cbMuted">
              Nothing is charged now. We’ll confirm the details and the price with you by email
              before anything is printed. Questions? Write to{' '}
              <a className="cbTextLink" href="mailto:info@getmorsel.com">
                info@getmorsel.com
              </a>
              .
            </p>
          )}
        </section>
      </form>
      <Link className="cbBack" to={-1}>
        <ArrowBack />
        Back to the preview
      </Link>
    </div>
  )
}

export default CheckoutStep
